import React, { useState, useEffect } from 'react';
import { Save, Globe, MessageSquare, Settings as SettingsIcon, FileText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { ImageUploadField } from './ImageUploadField';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const KOSOVO_CITIES = [
  'Prishtinë',
  'Prizren',
  'Pejë',
  'Gjakovë',
  'Gjilan',
  'Ferizaj',
  'Mitrovicë',
  'Podujevë',
  'Vushtrri',
  'Suharekë',
  'Rahovec',
  'Malishevë',
  'Lipjan',
  'Obiliq',
  'Fushë Kosovë',
  'Drenas',
  'Skenderaj',
  'Shtime',
  'Kaçanik',
  'Viti',
  'Kamenicë',
  'Novobërdë',
  'Graçanicë',
  'Shtërpcë',
  'Istog',
  'Klinë',
  'Deçan',
  'Junik',
  'Dragash',
  'Zubin Potok',
  'Zveçan',
  'Leposaviq',
  'Mitrovicë e Veriut',
  'Ranillug',
  'Partesh',
  'Kllokot',
  'Hani i Elezit',
  'Mamushë'
];

const safeParseJsonObject = (value) => {
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    return {};
  } catch {
    return {};
  }
};

export const AdminSettings = () => {
  const { token } = useAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [shippingCityRates, setShippingCityRates] = useState(() => ({}));

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/settings`);
      setSettings(response.data);
      setShippingCityRates(safeParseJsonObject(response.data?.shipping_city_rates));
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key, value, category) => {
    setSaving(true);
    try {
      await axios.post(
        `${API_URL}/settings`,
        { key, value, category },
        { headers: { Authorization: `Bearer ${token || localStorage.getItem('token')}` } }
      );
      setSettings((prev) => ({ ...prev, [key]: value }));
      toast.success('Setting updated');
    } catch (error) {
      toast.error('Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (key, category, overrideValue) => {
    const v = overrideValue !== undefined ? overrideValue : (settings[key] ?? '');
    updateSetting(key, v, category);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="font-serif text-3xl font-bold">Site Settings</h2>

      {/* Site Branding */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Site Branding & Identity
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="site_name">Site Name</Label>
              <div className="flex gap-2">
                <Input
                  id="site_name"
                  value={settings.site_name || ''}
                  onChange={(e) => setSettings({ ...settings, site_name: e.target.value })}
                  placeholder="NaturalCare"
                />
                <Button onClick={() => handleSave('site_name', 'branding')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Appears in navbar and browser tab</p>
            </div>
            <div>
              <Label htmlFor="site_name_full">Full Site Title</Label>
              <div className="flex gap-2">
                <Input
                  id="site_name_full"
                  value={settings.site_name_full || ''}
                  onChange={(e) => setSettings({ ...settings, site_name_full: e.target.value })}
                  placeholder="NaturalCare - Premium Natural Cosmetics"
                />
                <Button onClick={() => handleSave('site_name_full', 'branding')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Browser tab title</p>
            </div>
          </div>
          <div>
            <Label htmlFor="site_tagline">Site Tagline</Label>
            <div className="flex gap-2">
              <Input
                id="site_tagline"
                value={settings.site_tagline || ''}
                onChange={(e) => setSettings({ ...settings, site_tagline: e.target.value })}
                placeholder="Pure Wellness, Natural Beauty"
              />
              <Button onClick={() => handleSave('site_tagline', 'branding')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="logo_icon_text">Logo Icon Text</Label>
            <div className="flex gap-2">
              <Input
                id="logo_icon_text"
                value={settings.logo_icon_text || ''}
                onChange={(e) => setSettings({ ...settings, logo_icon_text: e.target.value })}
                placeholder="NC"
                maxLength={3}
              />
              <Button onClick={() => handleSave('logo_icon_text', 'branding')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">2-3 letters for logo icon (e.g., NC)</p>
          </div>
          <ImageUploadField
            label="Favicon (Browser Tab Icon)"
            value={settings.favicon_url || ''}
            onChange={(url) => setSettings({ ...settings, favicon_url: url })}
            onSave={(url) => handleSave('favicon_url', 'branding', url)}
            token={token}
            placeholder="/favicon.ico or image URL"
          />
          <p className="text-xs text-muted-foreground">
            💡 Recommended: 32x32px or 16x16px PNG/ICO file. Upload will work but for best results, place favicon.ico in public folder.
          </p>
        </CardContent>
      </Card>

      {/* Social Media */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            Social Media Links
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {['facebook', 'instagram', 'twitter', 'tiktok', 'youtube', 'linkedin'].map((platform) => (
            <div key={platform}>
              <Label htmlFor={`social_${platform}`} className="capitalize">{platform}</Label>
              <div className="flex gap-2">
                <Input
                  id={`social_${platform}`}
                  value={settings[`social_${platform}`] || ''}
                  onChange={(e) => setSettings({ ...settings, [`social_${platform}`]: e.target.value })}
                  placeholder={`https://${platform}.com/your-page`}
                />
                <Button onClick={() => handleSave(`social_${platform}`, 'social')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Contact Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Contact Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="contact_phone">Phone Number</Label>
            <div className="flex gap-2">
              <Input
                id="contact_phone"
                value={settings.contact_phone || ''}
                onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
              <Button onClick={() => handleSave('contact_phone', 'contact')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="contact_email">Email Address</Label>
            <div className="flex gap-2">
              <Input
                id="contact_email"
                value={settings.contact_email || ''}
                onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })}
                placeholder="support@naturalcare.com"
              />
              <Button onClick={() => handleSave('contact_email', 'contact')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="contact_address">Physical Address</Label>
            <div className="flex gap-2">
              <Textarea
                id="contact_address"
                value={settings.contact_address || ''}
                onChange={(e) => setSettings({ ...settings, contact_address: e.target.value })}
                placeholder="123 Natural Street, Wellness City, WC 12345"
                rows={2}
              />
              <Button onClick={() => handleSave('contact_address', 'contact')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Checkout Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" />
            Checkout Options
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="whatsapp_number">WhatsApp Number (with country code)</Label>
            <div className="flex gap-2">
              <Input
                id="whatsapp_number"
                value={settings.whatsapp_number || ''}
                onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })}
                placeholder="+355 69 123 4567"
              />
              <Button onClick={() => handleSave('whatsapp_number', 'checkout')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Orders can be sent via WhatsApp to this number
            </p>
          </div>
          <div>
            <Label htmlFor="order_email">Order Notification Email</Label>
            <div className="flex gap-2">
              <Input
                id="order_email"
                value={settings.order_email || ''}
                onChange={(e) => setSettings({ ...settings, order_email: e.target.value })}
                placeholder="orders@naturalcare.com"
              />
              <Button onClick={() => handleSave('order_email', 'checkout')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Orders can be sent via Gmail to this email
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Content Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Hero Section Content
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="hero_title">Hero Title</Label>
            <div className="flex gap-2">
              <Input
                id="hero_title"
                value={settings.hero_title || ''}
                onChange={(e) => setSettings({ ...settings, hero_title: e.target.value })}
                placeholder="Natural Beauty,"
              />
              <Button onClick={() => handleSave('hero_title', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="hero_title_accent">Hero Title Accent</Label>
            <div className="flex gap-2">
              <Input
                id="hero_title_accent"
                value={settings.hero_title_accent || ''}
                onChange={(e) => setSettings({ ...settings, hero_title_accent: e.target.value })}
                placeholder="Pure Wellness"
              />
              <Button onClick={() => handleSave('hero_title_accent', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="hero_subtitle">Hero Subtitle</Label>
            <div className="flex gap-2">
              <Textarea
                id="hero_subtitle"
                value={settings.hero_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, hero_subtitle: e.target.value })}
                placeholder="Discover premium cosmetics..."
                rows={3}
              />
              <Button onClick={() => handleSave('hero_subtitle', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* More Content Sections */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Page Content Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="shop_title">Shop Page Title</Label>
              <div className="flex gap-2">
                <Input
                  id="shop_title"
                  value={settings.shop_title || ''}
                  onChange={(e) => setSettings({ ...settings, shop_title: e.target.value })}
                />
                <Button onClick={() => handleSave('shop_title', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="featured_title">Featured Section Title</Label>
              <div className="flex gap-2">
                <Input
                  id="featured_title"
                  value={settings.featured_title || ''}
                  onChange={(e) => setSettings({ ...settings, featured_title: e.target.value })}
                />
                <Button onClick={() => handleSave('featured_title', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          
          <div>
            <Label htmlFor="shop_subtitle">Shop Page Subtitle</Label>
            <div className="flex gap-2">
              <Textarea
                id="shop_subtitle"
                value={settings.shop_subtitle || ''}
                onChange={(e) => setSettings({ ...settings, shop_subtitle: e.target.value })}
                rows={2}
              />
              <Button onClick={() => handleSave('shop_subtitle', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Business Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5" />
            Business Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="business_hours">Business Hours</Label>
            <div className="flex gap-2">
              <Input
                id="business_hours"
                value={settings.business_hours || ''}
                onChange={(e) => setSettings({ ...settings, business_hours: e.target.value })}
                placeholder="Mon-Fri: 9AM-6PM"
              />
              <Button onClick={() => handleSave('business_hours', 'contact')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="shipping_info">Shipping Information</Label>
            <div className="flex gap-2">
              <Textarea
                id="shipping_info"
                value={settings.shipping_info || ''}
                onChange={(e) => setSettings({ ...settings, shipping_info: e.target.value })}
                rows={2}
              />
              <Button onClick={() => handleSave('shipping_info', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          
          <div>
            <Label htmlFor="shipping_fee">Shipping Fee</Label>
            <div className="flex gap-2">
              <Input
                id="shipping_fee"
                type="number"
                step="0.01"
                value={settings.shipping_fee || ''}
                onChange={(e) => setSettings({ ...settings, shipping_fee: e.target.value })}
                placeholder="0.00"
              />
              <Button onClick={() => handleSave('shipping_fee', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Set to 0 for free shipping.
            </p>
          </div>


          <div>
            <Label htmlFor="free_shipping_threshold">Free Shipping Threshold</Label>
            <div className="flex gap-2">
              <Input
                id="free_shipping_threshold"
                type="number"
                step="0.01"
                value={settings.free_shipping_threshold || ''}
                onChange={(e) => setSettings({ ...settings, free_shipping_threshold: e.target.value })}
                placeholder="50.00"
              />
              <Button onClick={() => handleSave('free_shipping_threshold', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              If the order subtotal after discounts is ≥ this amount, shipping will be FREE. Set to 0 to disable.
            </p>
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <Label className="block">Per-City Shipping (Kosovo)</Label>
                <p className="text-xs text-muted-foreground">
                  Optional: set a shipping price per city. If a city is empty, the default Shipping Fee above is used.
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const v = Number(settings.shipping_fee || 0);
                    const next = {};
                    KOSOVO_CITIES.forEach((c) => { next[c] = v; });
                    setShippingCityRates(next);
                  }}
                  disabled={saving}
                >
                  Fill all = default
                </Button>
                <Button
                  type="button"
                  onClick={() => updateSetting('shipping_city_rates', JSON.stringify(shippingCityRates), 'content')}
                  disabled={saving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-72 overflow-auto pr-2">
              {KOSOVO_CITIES.map((city) => (
                <div key={city} className="flex items-center gap-2">
                  <span className="text-sm w-40 truncate" title={city}>{city}</span>
                  <Input
                    type="number"
                    step="0.01"
                    value={shippingCityRates?.[city] ?? ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      setShippingCityRates((prev) => ({ ...prev, [city]: val }));
                    }}
                    placeholder={String(settings.shipping_fee || '0')}
                  />
                </div>
              ))}
            </div>
          </div>

<div>
            <Label htmlFor="return_policy">Return Policy</Label>
            <div className="flex gap-2">
              <Textarea
                id="return_policy"
                value={settings.return_policy || ''}
                onChange={(e) => setSettings({ ...settings, return_policy: e.target.value })}
                rows={2}
              />
              <Button onClick={() => handleSave('return_policy', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Promotional Banner Content */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Promotional Banner (Full Control)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="promo_badge_text">Badge Text</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_badge_text"
                  value={settings.promo_badge_text || ''}
                  onChange={(e) => setSettings({ ...settings, promo_badge_text: e.target.value })}
                  placeholder="Limited Time Offer"
                />
                <Button onClick={() => handleSave('promo_badge_text', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="promo_title">Main Title</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_title"
                  value={settings.promo_title || ''}
                  onChange={(e) => setSettings({ ...settings, promo_title: e.target.value })}
                  placeholder="Up to 25% Off"
                />
                <Button onClick={() => handleSave('promo_title', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="promo_subtitle">Subtitle</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_subtitle"
                  value={settings.promo_subtitle || ''}
                  onChange={(e) => setSettings({ ...settings, promo_subtitle: e.target.value })}
                  placeholder="Premium Products"
                />
                <Button onClick={() => handleSave('promo_subtitle', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="promo_button_text">Button Text</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_button_text"
                  value={settings.promo_button_text || ''}
                  onChange={(e) => setSettings({ ...settings, promo_button_text: e.target.value })}
                  placeholder="Shop Deals Now"
                />
                <Button onClick={() => handleSave('promo_button_text', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Label htmlFor="promo_description">Description</Label>
            <div className="flex gap-2">
              <Textarea
                id="promo_description"
                value={settings.promo_description || ''}
                onChange={(e) => setSettings({ ...settings, promo_description: e.target.value })}
                rows={2}
                placeholder="Discover our exclusive selection..."
              />
              <Button onClick={() => handleSave('promo_description', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="promo_discount_text">Discount Text</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_discount_text"
                  value={settings.promo_discount_text || ''}
                  onChange={(e) => setSettings({ ...settings, promo_discount_text: e.target.value })}
                  placeholder="Save up to"
                />
                <Button onClick={() => handleSave('promo_discount_text', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div>
              <Label htmlFor="promo_discount_percentage">Discount Percentage</Label>
              <div className="flex gap-2">
                <Input
                  id="promo_discount_percentage"
                  value={settings.promo_discount_percentage || ''}
                  onChange={(e) => setSettings({ ...settings, promo_discount_percentage: e.target.value })}
                  placeholder="25%"
                />
                <Button onClick={() => handleSave('promo_discount_percentage', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <ImageUploadField
            label="Banner Image"
            value={settings.promo_image || ''}
            onChange={(url) => setSettings({ ...settings, promo_image: url })}
            onSave={(url) => handleSave('promo_image', 'content', url)}
            token={token}
            placeholder="https://example.com/banner.jpg"
          />
        </CardContent>
      </Card>

      {/* Testimonials Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Testimonials Section
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Section Headers */}
          <div className="pb-4 border-b">
            <h4 className="font-semibold mb-3">Section Headers</h4>
            <div className="space-y-3">
              <div>
                <Label htmlFor="testimonials_title">Section Title</Label>
                <div className="flex gap-2">
                  <Input
                    id="testimonials_title"
                    value={settings.testimonials_title || ''}
                    onChange={(e) => setSettings({ ...settings, testimonials_title: e.target.value })}
                    placeholder="What Our Customers Say"
                  />
                  <Button onClick={() => handleSave('testimonials_title', 'content')} disabled={saving}>
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <div>
                <Label htmlFor="testimonials_subtitle">Section Subtitle</Label>
                <div className="flex gap-2">
                  <Textarea
                    id="testimonials_subtitle"
                    value={settings.testimonials_subtitle || ''}
                    onChange={(e) => setSettings({ ...settings, testimonials_subtitle: e.target.value })}
                    rows={2}
                    placeholder="Real reviews from real customers..."
                  />
                  <Button onClick={() => handleSave('testimonials_subtitle', 'content')} disabled={saving}>
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Individual Testimonials */}
          {[1, 2, 3].map((num) => (
            <div key={num} className="pb-4 border-b last:border-0">
              <h4 className="font-semibold mb-3">Testimonial {num}</h4>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor={`testimonial_${num}_name`}>Customer Name</Label>
                  <div className="flex gap-2">
                    <Input
                      id={`testimonial_${num}_name`}
                      value={settings[`testimonial_${num}_name`] || ''}
                      onChange={(e) => setSettings({ ...settings, [`testimonial_${num}_name`]: e.target.value })}
                      placeholder="Customer Name"
                    />
                    <Button onClick={() => handleSave(`testimonial_${num}_name`, 'testimonials')} disabled={saving}>
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label htmlFor={`testimonial_${num}_role`}>Role/Status</Label>
                  <div className="flex gap-2">
                    <Input
                      id={`testimonial_${num}_role`}
                      value={settings[`testimonial_${num}_role`] || ''}
                      onChange={(e) => setSettings({ ...settings, [`testimonial_${num}_role`]: e.target.value })}
                      placeholder="Verified Customer"
                    />
                    <Button onClick={() => handleSave(`testimonial_${num}_role`, 'testimonials')} disabled={saving}>
                      <Save className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <Label htmlFor={`testimonial_${num}_text`}>Review Text</Label>
                <div className="flex gap-2">
                  <Textarea
                    id={`testimonial_${num}_text`}
                    value={settings[`testimonial_${num}_text`] || ''}
                    onChange={(e) => setSettings({ ...settings, [`testimonial_${num}_text`]: e.target.value })}
                    rows={3}
                    placeholder="Customer review text..."
                  />
                  <Button onClick={() => handleSave(`testimonial_${num}_text`, 'testimonials')} disabled={saving}>
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              <ImageUploadField
                label={`Testimonial ${num} - Customer Image`}
                value={settings[`testimonial_${num}_image`] || ''}
                onChange={(url) => setSettings({ ...settings, [`testimonial_${num}_image`]: url })}
                onSave={(url) => handleSave(`testimonial_${num}_image`, 'testimonials', url)}
                token={token}
                placeholder="https://example.com/customer.jpg"
              />
              <div className="mt-3">
                <Label htmlFor={`testimonial_${num}_verified`}>Verified Purchase</Label>
                <div className="flex gap-2">
                  <select
                    id={`testimonial_${num}_verified`}
                    value={settings[`testimonial_${num}_verified`] || 'true'}
                    onChange={(e) => setSettings({ ...settings, [`testimonial_${num}_verified`]: e.target.value })}
                    className="flex-1 px-4 py-2 rounded-lg border border-input bg-background"
                  >
                    <option value="true">Yes - Verified</option>
                    <option value="false">No - Not Verified</option>
                  </select>
                  <Button onClick={() => handleSave(`testimonial_${num}_verified`, 'testimonials')} disabled={saving}>
                    <Save className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* About Us (Homepage Section) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            About Us Section (Homepage)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="about_home_title">Section Title</Label>
            <div className="flex gap-2">
              <Input
                id="about_home_title"
                value={settings.about_home_title || ''}
                onChange={(e) => setSettings({ ...settings, about_home_title: e.target.value })}
                placeholder="Your Trusted Partner in Natural Wellness"
              />
              <Button onClick={() => handleSave('about_home_title', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="about_home_paragraph_1">First Paragraph</Label>
            <div className="flex gap-2">
              <Textarea
                id="about_home_paragraph_1"
                value={settings.about_home_paragraph_1 || ''}
                onChange={(e) => setSettings({ ...settings, about_home_paragraph_1: e.target.value })}
                rows={3}
                placeholder="At NaturalCare, we believe..."
              />
              <Button onClick={() => handleSave('about_home_paragraph_1', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="about_home_paragraph_2">Second Paragraph</Label>
            <div className="flex gap-2">
              <Textarea
                id="about_home_paragraph_2"
                value={settings.about_home_paragraph_2 || ''}
                onChange={(e) => setSettings({ ...settings, about_home_paragraph_2: e.target.value })}
                rows={3}
                placeholder="From premium cosmetics..."
              />
              <Button onClick={() => handleSave('about_home_paragraph_2', 'content')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="about_home_button_text">Button Text</Label>
              <div className="flex gap-2">
                <Input
                  id="about_home_button_text"
                  value={settings.about_home_button_text || ''}
                  onChange={(e) => setSettings({ ...settings, about_home_button_text: e.target.value })}
                  placeholder="Learn More About Us"
                />
                <Button onClick={() => handleSave('about_home_button_text', 'content')} disabled={saving}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
          <ImageUploadField
            label="About Section Image"
            value={settings.about_home_image || ''}
            onChange={(url) => setSettings({ ...settings, about_home_image: url })}
            onSave={(url) => handleSave('about_home_image', 'content', url)}
            token={token}
            placeholder="https://example.com/about.jpg"
          />
        </CardContent>
      </Card>

      {/* SEO Management */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            SEO Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="seo_home_title">Homepage Title</Label>
            <div className="flex gap-2">
              <Input
                id="seo_home_title"
                value={settings.seo_home_title || ''}
                onChange={(e) => setSettings({ ...settings, seo_home_title: e.target.value })}
                placeholder="NaturalCare - Premium Natural Cosmetics"
              />
              <Button onClick={() => handleSave('seo_home_title', 'seo')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="seo_home_description">Homepage Meta Description</Label>
            <div className="flex gap-2">
              <Textarea
                id="seo_home_description"
                value={settings.seo_home_description || ''}
                onChange={(e) => setSettings({ ...settings, seo_home_description: e.target.value })}
                rows={2}
                placeholder="Discover premium natural cosmetics..."
              />
              <Button onClick={() => handleSave('seo_home_description', 'seo')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="seo_home_keywords">Homepage Keywords</Label>
            <div className="flex gap-2">
              <Input
                id="seo_home_keywords"
                value={settings.seo_home_keywords || ''}
                onChange={(e) => setSettings({ ...settings, seo_home_keywords: e.target.value })}
                placeholder="natural cosmetics, organic skincare, wellness"
              />
              <Button onClick={() => handleSave('seo_home_keywords', 'seo')} disabled={saving}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Comma-separated keywords for SEO</p>
          </div>
        </CardContent>
      </Card>

      <div className="p-4 bg-muted/30 rounded-lg">
        <p className="text-sm text-muted-foreground">
          💡 <strong>Tip:</strong> Changes made here will be reflected across the website in real-time. 
          Social media links with empty values will not display on the website.
        </p>
      </div>
    </div>
  );
};